/**
 *  Scales a toewijzing's `volume` (part of the CDT_Omvang triple
 *  volume+eenheid+frequentie) into a total/monthly cap, per the `frequentie`
 *  codelist (COD757, confirmed against `waardelijst` label `Frequentie`):
 *  1=Per dag, 2=Per week, 3=Per 4 weken, 4=Per maand, 5=Per jaar,
 *  6=Totaal binnen geldigheid toewijzing.
 *
 *  Ported from `back-end/back-end/app/utils/omvangCap.ts` - keep both in
 *  sync, this repo has no shared package between front-end and back-end.
 *
 *  There is no external spec for exactly how gemeenten compute these caps
 *  (github #335 / findings.md flags this as needing product/UX input) - this
 *  is the most consistent reading of the codelist, treating each frequentie
 *  as "this many periods fit in the range, volume applies per period".
 */

function daysBetweenInclusive(start: Date, end: Date): number {
	const msPerDay = 24 * 60 * 60 * 1000;
	return Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
}

function distinctCalendarMonthsTouched(start: Date, end: Date): number {
	return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
}

function distinctCalendarYearsTouched(start: Date, end: Date): number {
	return end.getFullYear() - start.getFullYear() + 1;
}

function daysInMonth(monthStart: Date): number {
	return new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
}

/**
 *  Total cap over a toewijzing's whole `begindatum`-`einddatum` range.
 */
export function totalCap(volume: number, frequentie: number, begindatum: Date, einddatum: Date): number {
	switch (frequentie) {
		case 1: // Per dag
			return volume * daysBetweenInclusive(begindatum, einddatum);
		case 2: // Per week
			return volume * Math.ceil(daysBetweenInclusive(begindatum, einddatum) / 7);
		case 3: // Per 4 weken
			return volume * Math.ceil(daysBetweenInclusive(begindatum, einddatum) / 28);
		case 4: // Per maand
			return volume * distinctCalendarMonthsTouched(begindatum, einddatum);
		case 5: // Per jaar
			return volume * distinctCalendarYearsTouched(begindatum, einddatum);
		case 6: // Totaal binnen geldigheid toewijzing
			return volume;
		default:
			return volume;
	}
}

/**
 *  Cap for a single calendar month (identified by `monthStart`, any date within
 *  the target month - only the year/month are used). Returns `null` when the
 *  frequentie has no sub-month cadence to cap a single month against (per
 *  jaar / totaal binnen geldigheid toewijzing).
 */
export function monthCap(volume: number, frequentie: number, monthStart: Date): number | null {
	const daysThisMonth = daysInMonth(monthStart);
	switch (frequentie) {
		case 1: // Per dag
			return volume * daysThisMonth;
		case 2: // Per week
			return volume * Math.ceil(daysThisMonth / 7);
		case 3: // Per 4 weken
			return volume * Math.ceil(daysThisMonth / 28);
		case 4: // Per maand
			return volume;
		case 5: // Per jaar
		case 6: // Totaal binnen geldigheid toewijzing
			return null;
		default:
			return null;
	}
}
